import React from 'react';
import { computed, observable, action } from 'mobx';
import isEmpty from 'lodash/isEmpty';
import trim from 'lodash/trim';
import { extractFromSlug, extractFromUnderscoreSlug } from 'helpers/utils';
import {
  getManagerLiveClassReport,
  LiveClassReportItem,
} from 'services/LiveClassService';
import OrganizationStore from 'stores/Organization';

const STATUS_PRIORITY: Record<string, number> = {
  attended: 3,
  scheduled: 2,
  unattended: 1,
};

type RawLiveClassItem = LiveClassReportItem & {
  user?: string;
  user_name?: string;
  student?: string;
  student_username?: string;
  course?: string;
  slug?: string;
  offering_code?: string;
  sku_code?: string;
  course_code?: string;
  sku?: string;
  code?: string;
  course_version?: string;
  version?: string;
  course_sku?: string;
  state?: string;
  enrollment_status?: string;
  attendance_status?: string;
  class_status?: string;
};

type UserRow = { username: string } & Record<
  string,
  { status: string } | string | undefined
>;

type ColumnHeading = string | { courseLine: string; versionLine: string };

class LiveClassStatusTableStore {
  organizationStore: OrganizationStore;

  constructor(organizationStore: OrganizationStore) {
    this.organizationStore = organizationStore;
  }

  @observable userLiveClassTranslation = 'User name';

  @observable liveClassStatusesTranslation: {
    attended: string;
    scheduled: string;
  } = {
    attended: 'Attended',
    scheduled: 'Scheduled',
  };

  @observable versionTranslation = 'Version: ';

  @observable reporteesLiveClassStatus: RawLiveClassItem[] = [];

  static getItemUsername(item: RawLiveClassItem): string {
    return (
      item.username ||
      item.user ||
      item.user_name ||
      item.student ||
      item.student_username ||
      ''
    );
  }

  static getOfferingKey(item: RawLiveClassItem): string {
    const slug =
      item.course_slug || item.offering_slug || item.course || item.slug || '';
    if (slug) return slug;

    const sku =
      item.course_sku ||
      item.offering_code ||
      item.sku_code ||
      item.course_code ||
      item.sku ||
      item.code ||
      '';
    const version = item.course_version || item.version || '';

    if (sku && version) return `${sku}-${version}`;
    return sku;
  }

  static getSkuAndVersion(
    item: RawLiveClassItem,
    offeringKey: string,
  ): { code: string; version: string } {
    const key = offeringKey || '';
    const fromUnderscore = extractFromUnderscoreSlug(key);
    const fromHyphen = extractFromSlug(key);
    const parsed =
      fromUnderscore.code && fromUnderscore.version != null
        ? fromUnderscore
        : fromHyphen;

    let code =
      parsed?.code ||
      item.course_sku ||
      item.offering_code ||
      item.sku_code ||
      item.course_code ||
      item.sku ||
      item.code ||
      '';
    const version =
      parsed?.version || item.course_version || item.version || '';

    if (code?.slice?.(-2) === 'vc') {
      code = code.slice(0, -2);
    }

    return { code, version };
  }

  getColumnHeading(item: RawLiveClassItem, offeringKey: string): ColumnHeading {
    const { code, version } = LiveClassStatusTableStore.getSkuAndVersion(
      item,
      offeringKey,
    );
    if (!code) return '';

    if (!version) {
      return `${code}`;
    }

    return {
      courseLine: `${code}`,
      versionLine: `${this.versionTranslation}${version}`,
    };
  }

  static getItemStatus(item: RawLiveClassItem): string {
    const rawStatus = String(
      item.status ||
        item.state ||
        item.enrollment_status ||
        item.attendance_status ||
        item.class_status ||
        '',
    )
      .trim()
      .toLowerCase();

    if (
      ['unattended', 'unnatended', 'absent', 'no_show'].includes(rawStatus) ||
      rawStatus.includes('unattend') ||
      rawStatus.includes('unnatend')
    ) {
      return 'unattended';
    }
    if (
      ['attended', 'complete', 'completed', 'present'].includes(rawStatus) ||
      rawStatus.startsWith('attend')
    ) {
      return 'attended';
    }
    if (
      ['scheduled', 'enrolled', 'booked', 'reserved', 'upcoming'].includes(
        rawStatus,
      ) ||
      rawStatus.includes('schedul') ||
      rawStatus.includes('enroll')
    ) {
      return 'scheduled';
    }
    return rawStatus;
  }

  static getStatusByPriority(
    existingStatus: string | undefined,
    nextStatus: string | undefined,
  ): string | undefined {
    if (!nextStatus) return existingStatus;
    if (!existingStatus) return nextStatus;

    const existingPriority = STATUS_PRIORITY[existingStatus] || 0;
    const nextPriority = STATUS_PRIORITY[nextStatus] || 0;
    return nextPriority > existingPriority ? nextStatus : existingStatus;
  }

  @computed get transformedData(): UserRow[] {
    const selectedUsers = this.organizationStore.selectedUsersByManagerList;
    const selectedUsersByLower = selectedUsers.reduce<Record<string, string>>(
      (acc, username) => {
        acc[String(username).toLowerCase()] = username;
        return acc;
      },
      {},
    );

    if (selectedUsers.length === 0) {
      return [];
    }

    const result = selectedUsers.reduce<Record<string, UserRow>>(
      (acc, username) => {
        const userObject =
          this.organizationStore.getUserByUsername(username) || {};
        const preferredName = trim(userObject.preferred_name || '');
        acc[username] = {
          username: !isEmpty(preferredName)
            ? `${username} (${preferredName})`
            : `${username}`,
        };
        return acc;
      },
      {},
    );

    this.reporteesLiveClassStatus.forEach((item) => {
      const itemUsername = LiveClassStatusTableStore.getItemUsername(item);
      const selectedUsername =
        selectedUsersByLower[String(itemUsername).toLowerCase()];
      if (!selectedUsername) return;

      const offeringKey = LiveClassStatusTableStore.getOfferingKey(item);
      if (!offeringKey) return;

      const nextStatus = LiveClassStatusTableStore.getItemStatus(item);
      const existingStatus = (
        result[selectedUsername]?.[offeringKey] as
          | { status: string }
          | undefined
      )?.status;
      const finalStatus = LiveClassStatusTableStore.getStatusByPriority(
        existingStatus,
        nextStatus,
      );

      result[selectedUsername][offeringKey] = {
        status: finalStatus ?? '',
      };
    });

    return Object.values(result).sort((a, b) => {
      const fullNameA = a.username.replace(/.*\(|\).*/g, '');
      const fullNameB = b.username.replace(/.*\(|\).*/g, '');
      return fullNameA.localeCompare(fullNameB);
    });
  }

  @computed get tableColumns() {
    const selectedUsers = this.organizationStore.selectedUsersByManagerList;
    const selectedUsersSet = new Set(
      selectedUsers.map((username) => String(username).toLowerCase()),
    );

    const uniqueOfferings = this.reporteesLiveClassStatus
      .filter((item) =>
        selectedUsersSet.has(
          String(LiveClassStatusTableStore.getItemUsername(item)).toLowerCase(),
        ),
      )
      .filter(
        (item, index, self) =>
          self.findIndex(
            (x) =>
              LiveClassStatusTableStore.getOfferingKey(x) ===
              LiveClassStatusTableStore.getOfferingKey(item),
          ) === index,
      )
      .map((item) => {
        const offeringKey = LiveClassStatusTableStore.getOfferingKey(item);
        return {
          offeringKey,
          heading: this.getColumnHeading(item, offeringKey),
        };
      });

    const statusFormatter = (cell: { status: string } | undefined): string => {
      if (!cell?.status) return '';
      if (cell.status === 'attended') {
        return this.liveClassStatusesTranslation.attended;
      }
      if (cell.status === 'scheduled') {
        return this.liveClassStatusesTranslation.scheduled;
      }
      return '';
    };

    type TableColumn = {
      id: string;
      accessor: string | ((cellValue: UserRow) => UserRow[string]);
      Header: React.ReactNode;
      sort?: boolean;
      disableSortBy?: boolean;
      Cell?: (props: { cell?: { value?: { status: string } } }) => string;
      patternflyThProps: { className: string };
    };

    const offeringColumns: TableColumn[] = uniqueOfferings.map((offering) => {
      const { heading } = offering;
      let headerContent: React.ReactNode = offering.offeringKey;
      if (typeof heading === 'string' && heading) {
        headerContent = heading;
      } else if (
        heading &&
        typeof heading === 'object' &&
        heading.courseLine &&
        heading.versionLine
      ) {
        headerContent = (
          <React.Fragment>
            <span className="live-class-col-heading__course">
              {heading.courseLine}
            </span>
            <span className="live-class-col-heading__version">
              {heading.versionLine}
            </span>
          </React.Fragment>
        );
      }

      return {
        id: offering.offeringKey,
        accessor: (cellValue: UserRow) => cellValue[offering.offeringKey],
        Header: headerContent,
        Cell: (props: { cell?: { value?: { status: string } } }) =>
          statusFormatter(props?.cell?.value),
        disableSortBy: true,
        patternflyThProps: {
          className: 'custom-table-header',
        },
      };
    });

    const columns: TableColumn[] = [
      {
        id: 'username',
        accessor: 'username',
        Header: this.userLiveClassTranslation,
        sort: true,
        patternflyThProps: {
          className: 'custom-table-header-main',
        },
      },
      ...offeringColumns,
    ];

    return columns;
  }

  @action getReporteesLiveClassStatus = async (
    reportees: string[],
    signal?: AbortSignal,
  ): Promise<void> => {
    const organizationId = this.organizationStore.organization?.id;
    try {
      const items = await getManagerLiveClassReport({
        users: reportees,
        organizationId,
        signal,
      });
      this.reporteesLiveClassStatus = Array.isArray(items) ? items : [];
    } catch (err) {
      if (
        err?.name === 'AbortError' ||
        err?.name === 'CanceledError' ||
        err?.code === 'ERR_CANCELED'
      ) {
        return;
      }
      console.error('getReporteesLiveClassStatus error: ', err);
      this.reporteesLiveClassStatus = [];
    }
  };
}

export default LiveClassStatusTableStore;
